import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import { userType } from '../../types/Types';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import AppBar from '../organisms/AppBar';
import Drawer from '../organisms/Drawer';
import InputDialog from '../atoms/InputDialog';
import CommonDialog from '../atoms/CommonDialog';
import Copyright from '../molecules/Copyright';
import Log from '../../lib/log';
import {
  Button,
  Stack,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useSelector } from '../../store/store';
import { setUser } from '../../store/loginUserSlice';
import { useDispatch } from 'react-redux';
import { v2api } from '../../api/axios';
import { sendDeletedMail } from '../../api/sendMail';
import { formatNumber } from '../../functions/Utils';

// ログの初期化
const log = new Log('Account');

function AccountContent() {
  log.putLog('info', 'Account started.');

  // ストアからユーザー情報を取得
  const user = useSelector((state) => state.loginUser.user);
  const dispatch = useDispatch();

  // 最新のユーザー情報を取得する
  const [currentUser, setCurrentUser] = useState<userType | undefined>();
  useEffect(() => {
    (async () => {
      try {
        // 最新のユーザー情報を取得する
        const postData = {
          userId: user.id,
          token: user.access_token,
        };
        const res = await v2api.post('/get-user', postData);
        if (res.data.statusCode === 200) {
          // 取得成功
          const body = JSON.parse(res.data.body);
          setCurrentUser(body);
        } else {
          throw new Error(`getUser error: ${res.data.statusCode}`);
        }
      } catch (err: any) {
        log.putLog('error', err.message ? err.message : err);
        // ストアを初期化
        dispatch(setUser(''));
      }
    })();
  }, [user, dispatch]);

  const [userStatus, setUserStatus] = useState('ご利用中');

  useEffect(() => {
    switch (currentUser?.user_status) {
      case 0:
        setUserStatus('セットアップ中');
        setCancelButtonDisable(true);
        break;
      case 1:
        setUserStatus('ご利用中');
        setCancelButtonDisable(false);
        break;
      case 2:
        setUserStatus('解約受付済');
        setCancelButtonDisable(true);
        break;
      case 3:
        setUserStatus('解約済');
        setCancelButtonDisable(true);
        break;
      default:
        break;
    }
  }, [currentUser]);

  // 契約電話番号

  // 解約
  const [cancelButtonDisable, setCancelButtonDisable] = useState(false);
  const handleCancelClick = async (e: any) => {
    if (cancelReasonDisable) {
      setCancelReasonDisable(false); // キャンセル理由を入力させるためにテキストボックスを表示
      setCancelButtonDisable(true); // ボタンはDisableにしておく
    } else {
      // 解約理由が入っていることを確認
      if (inputCancelReasonRef.current.value !== '') {
        setDeleteDialogOpen(true);
      } else {
        alert('解約理由を入力してください。');
        return -1;
      }
    }
  };
  const handleDeleteConfirm = async (result: boolean) => {
    if (result) {
      try {
        // ユーザー情報を更新する
        currentUser!.cancel_reason = inputCancelReasonRef.current.value;
        currentUser!.user_status = 2; // 解約処理中
        let query = `
          mutation UpdateDB (
            $id: String!,
            $user_status: Int,
            $cancel_reason: String
          ) {
            updateUsers(
              input: {
                id: $id,
                user_status: $user_status,
                cancel_reason: $cancel_reason
              }
            ) {
              id
            }
          }
        `;
        const postData = {
          userId: currentUser?.id,
          token: currentUser?.access_token,
          query,
          variables: {
            id: currentUser?.id,
            user_status: currentUser?.user_status,
            cancel_reason: currentUser?.cancel_reason,
          },
        };
        const res = await v2api.post('/update-db', postData);
        if (res.data.statusCode === 200) {
          // 更新成功
          if (currentUser?.recipients) {
            // 解約受付メール送信
            await sendDeletedMail(
              currentUser.id,
              currentUser.access_token,
              currentUser.recipients[0].address,
            );
            // Slackで通知
            const message = `user: ${currentUser.id} canceled. \n cancelReason: ${currentUser?.cancel_reason}`;
            await sendSlack(message);
          }
          dispatch(setUser(currentUser));
        } else {
          throw new Error(`update-db error: ${res.data.statusCode}`);
        }
      } catch (err: any) {
        log.putLog('error', err.message ? err.message : err);
        // ストアを初期化
        dispatch(setUser(''));
      }
    } else {
      currentUser!.cancel_reason = '';
      setCancelReasonDisable(true); // キャンセル理由を入力させるためにテキストボックスを非表示
    }
    setDeleteDialogOpen(false);
  };

  /**
   * Slack経由でメッセージを通知
   * @param message 送信したいメッセージ
   */
  const sendSlack = async (message: string) => {
    const postData = {
      message,
    };
    await v2api.post('/send-slack', postData);
  };

  // 解約理由
  const [cancelReasonDisable, setCancelReasonDisable] = useState(true);
  const inputCancelReasonRef = useRef<any>(null);
  const [cancelReasonError, setCancelReasonError] = useState(true);
  const handleCancelReasonChange = (e: any) => {
    e.preventDefault();
    // エラーチェック
    if (inputCancelReasonRef.current.value.trim() === '') {
      setCancelReasonError(true);
      setCancelButtonDisable(true);
    } else {
      setCancelReasonError(false);
      setCancelButtonDisable(false);
    }
  };

  // ダイヤログ
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // ニックネーム
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const handleEdit = (e: any) => {
    e.preventDefault();
    setNameDialogOpen(true);
  };
  const handleConfirm = async (
    result: boolean,
    inputData: string | undefined,
  ) => {
    setNameDialogOpen(false);
    if (result) {
      try {
        // ユーザー情報を更新する
        currentUser!.user_name = inputData;
        let query = `
          mutation UpdateDB (
            $id: String!,
            $user_name: String
          ) {
            updateUsers(
              input: {
                id: $id,
                user_name: $user_name
              }
            ) {
              id
            }
          }
        `;
        const postData = {
          userId: currentUser?.id,
          token: currentUser?.access_token,
          query,
          variables: {
            id: currentUser?.id,
            user_name: currentUser?.user_name,
          },
        };
        const res = await v2api.post('/update-db', postData);
        if (res.data.statusCode === 200) {
          // 更新成功
          dispatch(setUser(currentUser));
        } else {
          throw new Error(`update-db error: ${res.data.statusCode}`);
        }
      } catch (err: any) {
        log.putLog('error', err.message ? err.message : err);
        // ストアを初期化
        dispatch(setUser(''));
      }
    }
  };

  // 領収書宛先
  const [receiptNameDialogOpen, setReceiptNameDialogOpen] = useState(false);
  const handleReceiptNameEdit = (e: any) => {
    e.preventDefault();
    setReceiptNameDialogOpen(true);
  };
  const handleReceiptNameConfirm = async (
    result: boolean,
    inputData: string | undefined,
  ) => {
    setReceiptNameDialogOpen(false);
    if (result) {
      try {
        // ユーザー情報を更新する
        currentUser!.receipt_name = inputData;
        let query = `
          mutation UpdateDB (
            $id: String!,
            $receipt_name: String
          ) {
            updateUsers(
              input: {
                id: $id,
                receipt_name: $receipt_name
              }
            ) {
              id
            }
          }
        `;
        const postData = {
          userId: currentUser?.id,
          token: currentUser?.access_token,
          query,
          variables: {
            id: currentUser?.id,
            receipt_name: currentUser?.receipt_name || '',
          },
        };
        const res = await v2api.post('/update-db', postData);
        if (res.data.statusCode === 200) {
          // 更新成功
          dispatch(setUser(currentUser));
        } else {
          throw new Error(`update-db error: ${res.data.statusCode}`);
        }
      } catch (err: any) {
        log.putLog('error', err.message ? err.message : err);
        // ストアを初期化
        dispatch(setUser(''));
      }
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar title='アカウント設定' />
      <Drawer />
      <Box
        component='main'
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
          flexGrow: 1,
          height: '100vh',
          overflow: 'auto',
        }}
      >
        <Toolbar />
        <Container maxWidth='lg' sx={{ mt: 4, mb: 4 }}>
          <InputDialog
            openDialog={nameDialogOpen}
            title='ニックネーム'
            defaultValue={currentUser?.user_name}
            message='ニックネームを入力してください'
            handleConfirm={(result, inputData) =>
              handleConfirm(result, inputData)
            }
          />
          <InputDialog
            openDialog={receiptNameDialogOpen}
            title='領収書宛名'
            defaultValue={currentUser?.receipt_name || ''}
            message='領収書の宛名を入力してください'
            handleConfirm={(result, inputData) =>
              handleReceiptNameConfirm(result, inputData)
            }
          />
          <CommonDialog
            openDialog={deleteDialogOpen}
            title={'解約確認'}
            message={'本当に解約しますか？'}
            handleConfirm={(result) => handleDeleteConfirm(result)}
          />
          <Grid container spacing={3}>
            {/* Chart */}
            <Grid item xs={12} md={6} lg={6}>
              <Paper
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 'auto',
                }}
              >
                <Typography
                  variant='h6'
                  gutterBottom
                  component='div'
                  color={'gray'}
                >
                  現在のステータス: {userStatus}
                </Typography>
              </Paper>
              <Paper
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 'auto',
                  marginTop: 1,
                }}
              >
                <Stack
                  direction='row'
                  alignContent='center'
                  alignItems='flex-start'
                  spacing={1}
                >
                  <Typography
                    variant='h6'
                    gutterBottom
                    component='div'
                    color={'gray'}
                  >
                    ニックネーム: {currentUser?.user_name}
                  </Typography>
                  <IconButton
                    color='primary'
                    aria-label='edit'
                    component='span'
                    onClick={handleEdit}
                  >
                    <EditIcon fontSize='small' />
                  </IconButton>
                </Stack>
              </Paper>
              <Paper
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 'auto',
                  marginTop: 1,
                }}
              >
                <Stack
                  direction='row'
                  alignContent='center'
                  alignItems='flex-start'
                  spacing={1}
                >
                  <Typography
                    variant='h6'
                    gutterBottom
                    component='div'
                    color={'gray'}
                  >
                    領収書宛名: {currentUser?.receipt_name || ''}
                  </Typography>
                  <IconButton
                    color='primary'
                    aria-label='edit'
                    component='span'
                    onClick={handleReceiptNameEdit}
                  >
                    <EditIcon fontSize='small' />
                  </IconButton>
                </Stack>
              </Paper>
              <Paper
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 'auto',
                  marginTop: 1,
                }}
              >
                <Stack
                  direction='row'
                  alignContent='center'
                  alignItems='flex-start'
                  spacing={1}
                >
                  <Typography
                    variant='h6'
                    gutterBottom
                    component='div'
                    color={'gray'}
                  >
                    転送先番号:
                  </Typography>
                  <Typography
                    variant='h6'
                    gutterBottom
                    component='div'
                    color={'blue'}
                  >
                    {formatNumber(currentUser?.service_number)}
                  </Typography>
                </Stack>
                間違い電話が多いなどの理由で050番号を変更されたい方は、お手数ですが右上のヘルプアイコンからチケットを送信してください。
              </Paper>
              <Paper
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 'auto',
                  marginTop: 1,
                }}
              >
                <Typography
                  variant='h6'
                  gutterBottom
                  component='div'
                  color={'gray'}
                >
                  招待コード: {currentUser?.invitation_code}
                </Typography>
                この招待コードを使って新規登録がされると、招待した方とあなたの両方に1ヶ月分の無料期間が追加されます。
              </Paper>
            </Grid>
            {/* Recent Deposits */}
            <Grid item xs={12} md={6} lg={6}>
              <Paper
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 'auto',
                }}
              >
                <Typography
                  variant='h6'
                  gutterBottom
                  component='div'
                  color={'gray'}
                >
                  パスワードの変更
                </Typography>
                パスワードを変更するには、ご契約いただいた電話番号（
                {user.user_number}
                ）より、以下の番号に電話をかけて音声に従ってパスワードを変更してください。
                <Typography
                  variant='h6'
                  gutterBottom
                  component='div'
                  color={'blue'}
                >
                  {process.env.REACT_APP_RESET_NUMBER}
                </Typography>
                <Typography gutterBottom component='div' color={'gray'}>
                  ※ご契約番号が変更になった場合は、お手数ですがこちらから個別にサポートまでご連絡ください。
                </Typography>
                <Stack spacing={2}>
                  {!cancelReasonDisable ? (
                    <div>
                      解約理由
                      <TextField
                        error={cancelReasonError}
                        inputProps={{ minLength: 1 }}
                        inputRef={inputCancelReasonRef}
                        fullWidth
                        id='cancelReason'
                        multiline
                        rows={5}
                        onChange={handleCancelReasonChange}
                        defaultValue=''
                        disabled={currentUser?.user_status === 2}
                      />
                    </div>
                  ) : (
                    ''
                  )}
                  <Button
                    variant='outlined'
                    color='secondary'
                    size='small'
                    onClick={handleCancelClick}
                    disabled={cancelButtonDisable}
                  >
                    解約する
                  </Button>
                </Stack>
              </Paper>
            </Grid>
            {/* <Grid item xs={12}>
              <Paper
                sx={{ p: 2, display: 'flex', flexDirection: 'column' }}
              ></Paper>
            </Grid> */}
          </Grid>
          <Copyright sx={{ pt: 4 }} />
        </Container>
      </Box>
    </Box>
  );
}

export default function Account() {
  return <AccountContent />;
}
